/**
 * Scrydex Pricing Component
 * 
 * Displays comprehensive pricing data with:
 * - Tabbed interface (Raw, PSA, CGC, TAG, etc.)
 * - Interactive price chart
 * - Condition/grade breakdown with prices
 */

import { useState, useEffect, useMemo, useRef } from 'react';
import { TrendingUp, TrendingDown, Minus, Info, RefreshCw } from 'lucide-react';
import { getCardPrices, getCardPriceHistory, fetchAndStorePrices } from '../services/scrydexApi';
import { 
  CardPricingData, 
  PriceHistoryPoint, 
  PriceTrends,
  CONDITION_LABELS, 
  RawCondition,
  GradingCompany,
  getFinishLabel
} from '../types/scrydex';
import { useCurrencyStore, Currency } from '../lib/currencyStore';
import { formatPrice } from '../utils/formatPrice';
import { GradingIcon } from './icons/GradingIcon';

// =====================================================
// PRICE CHART COMPONENT
// =====================================================

interface ChartLine {
  label: string;
  color: string;
  data: PriceHistoryPoint[];
}

interface PriceChartProps {
  lines: ChartLine[];
  height?: number;
}

// Catmull-Rom spline for smooth natural curves
function createSmoothPath(points: { x: number; y: number }[]): string {
  if (points.length < 2) return '';
  if (points.length === 2) {
    return `M ${points[0].x} ${points[0].y} L ${points[1].x} ${points[1].y}`;
  }

  // Use Catmull-Rom to Bezier conversion for smoother curves
  const tension = 0.5; // Higher = smoother curves
  let path = `M ${points[0].x} ${points[0].y}`;
  
  for (let i = 0; i < points.length - 1; i++) {
    const p0 = points[Math.max(0, i - 1)];
    const p1 = points[i];
    const p2 = points[i + 1];
    const p3 = points[Math.min(points.length - 1, i + 2)];

    // Catmull-Rom to cubic bezier conversion
    const cp1x = p1.x + (p2.x - p0.x) / 6 * tension * 3;
    const cp1y = p1.y + (p2.y - p0.y) / 6 * tension * 3;
    const cp2x = p2.x - (p3.x - p1.x) / 6 * tension * 3;
    const cp2y = p2.y - (p3.y - p1.y) / 6 * tension * 3;

    path += ` C ${cp1x} ${cp1y}, ${cp2x} ${cp2y}, ${p2.x} ${p2.y}`;
  }

  return path;
}

// Cubic interpolation for smooth value transitions
function cubicInterpolate(p0: number, p1: number, p2: number, p3: number, t: number): number {
  const t2 = t * t;
  const t3 = t2 * t;
  return 0.5 * (
    (2 * p1) +
    (-p0 + p2) * t +
    (2 * p0 - 5 * p1 + 4 * p2 - p3) * t2 +
    (-p0 + 3 * p1 - 3 * p2 + p3) * t3
  );
}

// Interpolate points for smoother chart using cubic interpolation
function interpolatePoints(points: { x: number; y: number; price: number; date: string }[], numPoints: number = 60): { x: number; y: number; price: number; date: string }[] {
  if (points.length < 2) return points;
  if (points.length === 2) {
    // Simple linear for 2 points
    const result: { x: number; y: number; price: number; date: string }[] = [];
    for (let i = 0; i < numPoints; i++) {
      const t = i / (numPoints - 1);
      result.push({
        x: points[0].x + (points[1].x - points[0].x) * t,
        y: points[0].y + (points[1].y - points[0].y) * t,
        price: points[0].price + (points[1].price - points[0].price) * t,
        date: new Date(new Date(points[0].date).getTime() + (new Date(points[1].date).getTime() - new Date(points[0].date).getTime()) * t).toISOString().split('T')[0]
      });
    }
    return result;
  }
  
  const result: { x: number; y: number; price: number; date: string }[] = [];
  
  for (let i = 0; i < numPoints; i++) {
    const t = i / (numPoints - 1);
    const totalX = t * 100;
    
    // Find segment index
    let segIndex = 0;
    for (let j = 0; j < points.length - 1; j++) {
      if (points[j + 1].x >= totalX) {
        segIndex = j;
        break;
      }
      segIndex = j;
    }
    
    // Get 4 points for cubic interpolation
    const p0 = points[Math.max(0, segIndex - 1)];
    const p1 = points[segIndex];
    const p2 = points[Math.min(segIndex + 1, points.length - 1)];
    const p3 = points[Math.min(segIndex + 2, points.length - 1)];
    
    // Local t within segment
    const segmentT = p2.x === p1.x ? 0 : Math.max(0, Math.min(1, (totalX - p1.x) / (p2.x - p1.x)));
    
    // Cubic interpolation for smooth values
    const y = cubicInterpolate(p0.y, p1.y, p2.y, p3.y, segmentT);
    const price = cubicInterpolate(p0.price, p1.price, p2.price, p3.price, segmentT);
    
    // Linear date interpolation
    const date1 = new Date(p1.date).getTime();
    const date2 = new Date(p2.date).getTime();
    const dateMs = date1 + (date2 - date1) * segmentT;
    
    result.push({
      x: totalX,
      y,
      price: Math.max(0, price), // Ensure no negative prices
      date: new Date(dateMs).toISOString().split('T')[0]
    });
  }
  
  return result;
}

function PriceChart({ lines, height = 200 }: PriceChartProps) {
  const [hoveredX, setHoveredX] = useState<number | null>(null);

  // Filter to lines with enough data
  const validLines = lines.filter(line => {
    const valid = line.data.filter(d => d.market && d.market > 0);
    return valid.length >= 2;
  });

  if (validLines.length === 0) {
    return (
      <div 
        className="flex items-center justify-center bg-white/5 rounded-lg text-white/40 text-sm"
        style={{ height }}
      >
        <span>Chart data will appear after price history is collected</span>
      </div>
    );
  }

  // Compute global date range and price range across all lines
  const allValidData = validLines.flatMap(l => l.data.filter(d => d.market && d.market > 0));
  const allDates = allValidData.map(d => new Date(d.date).getTime());
  const globalMinDate = Math.min(...allDates);
  const globalMaxDate = Math.max(...allDates);
  const dateRange = globalMaxDate - globalMinDate || 1;

  const allPrices = allValidData.map(d => d.market!);
  const maxPrice = Math.max(...allPrices);
  const yMax = maxPrice + maxPrice * 0.1;

  // Build processed line data (points, paths, interpolated) for each line
  const processedLines = validLines.map(line => {
    const validData = line.data.filter(d => d.market && d.market > 0);
    const basePoints = validData.map(d => {
      const x = ((new Date(d.date).getTime() - globalMinDate) / dateRange) * 100;
      const y = 100 - ((d.market! / yMax) * 100);
      return { x, y, price: d.market!, date: d.date };
    });

    const interpolated = interpolatePoints(basePoints, 50);
    const smoothPath = createSmoothPath(interpolated);
    const areaPath = smoothPath + ` L 100 100 L 0 100 Z`;

    return { ...line, validData, basePoints, interpolated, smoothPath, areaPath };
  });

  // For X-axis labels, use the global date range
  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    const day = date.getDate();
    const month = date.toLocaleDateString('en-US', { month: 'short' });
    const year = date.getFullYear().toString().slice(-2);
    return `${month} ${day} '${year}`;
  };

  const globalStartDate = new Date(globalMinDate).toISOString().split('T')[0];
  const globalMidDate = new Date(globalMinDate + dateRange / 2).toISOString().split('T')[0];
  const globalEndDate = new Date(globalMaxDate).toISOString().split('T')[0];

  // Handle mouse move
  const handleMouseMove = (e: React.MouseEvent<SVGSVGElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    setHoveredX(x);
  };

  // For each line, find the interpolated point closest to hovered X
  const hoveredPoints = hoveredX !== null ? processedLines.map(pl => {
    let closest = pl.interpolated[0];
    let closestDist = Infinity;
    for (const p of pl.interpolated) {
      const dist = Math.abs(p.x - hoveredX);
      if (dist < closestDist) { closestDist = dist; closest = p; }
    }
    return { label: pl.label, color: pl.color, point: closest };
  }) : [];

  const singleLine = processedLines.length === 1;
  const areaOpacity = singleLine ? 1 : 0.5;

  return (
    <div className="relative" style={{ height }}>
      {/* Legend */}
      <div className="absolute top-2 right-2 flex items-center gap-3 text-xs z-10">
        {processedLines.map(pl => (
          <div key={pl.label} className="flex items-center gap-1.5">
            <span className="w-4 h-0.5 rounded" style={{ backgroundColor: pl.color }} />
            <span className="text-white/60">{pl.label}</span>
          </div>
        ))}
      </div>

      {/* Y-axis labels */}
      {(() => {
        const getNiceTicks = (min: number, max: number, tickCount: number = 3) => {
          const range = max - min;
          const roughStep = range / (tickCount - 1);
          const magnitude = Math.pow(10, Math.floor(Math.log10(roughStep)));
          const residual = roughStep / magnitude;
          let niceStep: number;
          if (residual <= 1.5) niceStep = 1 * magnitude;
          else if (residual <= 3) niceStep = 2 * magnitude;
          else if (residual <= 7) niceStep = 5 * magnitude;
          else niceStep = 10 * magnitude;
          const niceMin = Math.floor(min / niceStep) * niceStep;
          const niceMax = Math.ceil(max / niceStep) * niceStep;
          const ticks: number[] = [];
          for (let v = niceMin; v <= niceMax; v += niceStep) ticks.push(v);
          if (ticks.length < 3 && ticks.length >= 2) {
            ticks.splice(1, 0, (ticks[0] + ticks[ticks.length - 1]) / 2);
          }
          return ticks.slice(0, 4);
        };
        const ticks = getNiceTicks(0, maxPrice);
        const formatTickValue = (val: number) => {
          if (val >= 100) return `$${Math.round(val).toLocaleString()}`;
          return `$${val.toFixed(2)}`;
        };
        return (
          <div className="absolute left-0 top-2 bottom-6 w-16 flex flex-col justify-between text-xs text-white/40">
            {ticks.reverse().map((tick, i) => (
              <span key={i}>{formatTickValue(tick)}</span>
            ))}
          </div>
        );
      })()}

      {/* Chart */}
      <div className="ml-[72px] h-full pb-6 relative">
        <svg 
          viewBox="0 0 100 100" 
          preserveAspectRatio="none"
          className="w-full h-full cursor-crosshair"
          onMouseMove={handleMouseMove}
          onMouseLeave={() => setHoveredX(null)}
        >
          <defs>
            {processedLines.map((pl, i) => (
              <linearGradient key={i} id={`chartGrad-${i}`} x1="0" x2="0" y1="0" y2="1">
                <stop offset="0%" stopColor={pl.color} stopOpacity={singleLine ? 0.25 : 0.12} />
                <stop offset="100%" stopColor={pl.color} stopOpacity="0.02" />
              </linearGradient>
            ))}
          </defs>
          
          {/* Grid lines */}
          {[20, 40, 60, 80].map(y => (
            <line key={y} x1="0" y1={y} x2="100" y2={y} stroke="white" strokeOpacity="0.05" strokeWidth="0.5" vectorEffect="non-scaling-stroke" />
          ))}
          
          {/* Render each line */}
          {processedLines.map((pl, i) => (
            <g key={i} opacity={areaOpacity}>
              <path d={pl.areaPath} fill={`url(#chartGrad-${i})`} />
              <path
                d={pl.smoothPath}
                fill="none"
                stroke={pl.color}
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                vectorEffect="non-scaling-stroke"
              />
            </g>
          ))}

          {/* Hover indicator line */}
          {hoveredX !== null && (
            <line 
              x1={hoveredX} y1="0" x2={hoveredX} y2="100" 
              stroke="white" strokeOpacity="0.15" strokeWidth="1"
              vectorEffect="non-scaling-stroke"
            />
          )}
        </svg>

        {/* X-axis labels */}
        <div className="flex justify-between text-xs text-white/40 mt-1">
          <span>{formatDate(globalStartDate)}</span>
          <span>{formatDate(globalMidDate)}</span>
          <span>{formatDate(globalEndDate)}</span>
        </div>
      </div>

      {/* Tooltip */}
      {hoveredPoints.length > 0 && (
        <div 
          className="absolute bg-black/95 border border-white/20 rounded-lg px-3 py-2 text-xs pointer-events-none z-20 shadow-lg"
          style={{
            left: `calc(${Math.min(Math.max(hoveredPoints[0].point.x, 15), 85)}% + 72px)`,
            top: '8px',
            transform: 'translateX(-50%)'
          }}
        >
          <div className="text-white/50 mb-1">{formatDate(hoveredPoints[0].point.date)}</div>
          {hoveredPoints.map(hp => (
            <div key={hp.label} className="flex items-center gap-2">
              <span className="w-2 h-2 rounded-full" style={{ backgroundColor: hp.color }} />
              <span className="text-white/60">{hp.label}</span>
              <span className="text-white font-semibold ml-auto">${hp.point.price.toFixed(2)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// =====================================================
// GENERATE SYNTHETIC CHART DATA FROM TRENDS
// =====================================================

/**
 * Generate synthetic price history data from trend percentages
 * This allows us to show a chart even when historical data hasn't been collected yet
 */
function generateSyntheticHistory(
  currentPrice: number,
  trends: PriceTrends | null
): PriceHistoryPoint[] {
  if (!currentPrice || currentPrice <= 0) return [];
  
  const today = new Date();
  const points: PriceHistoryPoint[] = [];
  
  // Define the trend periods we want to use (in days)
  const trendPeriods = [
    { days: 180, key: 'days_180' },
    { days: 90, key: 'days_90' },
    { days: 30, key: 'days_30' },
    { days: 14, key: 'days_14' },
    { days: 7, key: 'days_7' },
    { days: 1, key: 'days_1' },
  ] as const;
  
  // Calculate historical prices from trends
  for (const period of trendPeriods) {
    const trendData = trends?.[period.key as keyof PriceTrends];
    if (trendData && typeof trendData.percent_change === 'number') {
      const percentChange = trendData.percent_change;
      // If current price is X and it changed by Y%, then past price was: current / (1 + Y/100)
      const pastPrice = currentPrice / (1 + percentChange / 100);
      
      const pastDate = new Date(today);
      pastDate.setDate(pastDate.getDate() - period.days);
      
      points.push({
        date: pastDate.toISOString().split('T')[0],
        low: null,
        market: pastPrice,
        high: null,
      });
    }
  }
  
  // Add current price point
  points.push({
    date: today.toISOString().split('T')[0],
    low: null,
    market: currentPrice,
    high: null,
  });
  
  // Sort by date ascending
  points.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
  
  return points;
}

// =====================================================
// TREND INDICATOR
// =====================================================

interface TrendIndicatorProps {
  percentChange: number;
  size?: 'sm' | 'md';
}

function TrendIndicator({ percentChange, size = 'sm' }: TrendIndicatorProps) {
  const isPositive = percentChange > 0;
  const isNeutral = percentChange === 0;
  
  const colorClass = isNeutral 
    ? 'text-white/40' 
    : isPositive 
      ? 'text-green-500' 
      : 'text-red-500';

  const Icon = isNeutral ? Minus : isPositive ? TrendingUp : TrendingDown;
  const iconSize = size === 'sm' ? 12 : 16;

  return (
    <div className={`flex items-center gap-1 ${colorClass}`}>
      <Icon size={iconSize} />
      <span className={size === 'sm' ? 'text-xs' : 'text-sm'}>
        {isPositive ? '+' : ''}{percentChange.toFixed(1)}%
      </span>
    </div>
  );
}

// =====================================================
// CONDITION PRICE CARD
// =====================================================


// =====================================================
// GRADED PRICE ROW
// =====================================================

interface GradedPriceRowProps {
  grade: string;
  marketPrice: number | null;
  lowPrice: number | null;
  highPrice: number | null;
  trends: PriceTrends | null;
  currency: Currency;
}

function GradedPriceRow({ grade, marketPrice, lowPrice, highPrice, trends, currency }: GradedPriceRowProps) {
  if (!marketPrice) return null;

  return (
    <div className="flex items-center justify-between py-2 sm:py-3 border-b border-white/5 last:border-0">
      <div className="flex items-center gap-2 sm:gap-3">
        <span className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg bg-gradient-to-br from-amber-500/20 to-amber-600/20 border border-amber-500/30 flex items-center justify-center font-bold text-amber-400 text-xs sm:text-sm">
          {grade}
        </span>
        <div>
          <div className="font-medium text-white text-sm sm:text-base">Grade {grade}</div>
          {lowPrice && highPrice && (
            <div className="text-[10px] sm:text-xs text-white/40">
              {formatPrice(lowPrice, currency)} - {formatPrice(highPrice, currency)}
            </div>
          )}
        </div>
      </div>
      <div className="text-right">
        <div className="font-bold text-white text-sm sm:text-base">{formatPrice(marketPrice, currency)}</div>
        {trends?.days_30 && (
          <TrendIndicator percentChange={trends.days_30.percent_change} />
        )}
      </div>
    </div>
  );
}

// =====================================================
// MAIN COMPONENT
// =====================================================

interface ScrydexPricingProps {
  cardId: string;
}

type PriceTab = 'Raw' | 'PSA' | 'CGC' | 'BGS' | 'TAG' | 'SGC';
type ChartTimeFrame = '7D' | '1M' | '3M' | '6M' | '1Y' | 'ALL';

const TAB_ORDER: PriceTab[] = ['Raw', 'PSA', 'CGC', 'BGS', 'TAG', 'SGC'];
const TIME_FRAME_DAYS: Record<ChartTimeFrame, number> = {
  '7D': 7, '1M': 30, '3M': 90, '6M': 180, '1Y': 365, 'ALL': 9999
};

const CONDITION_COLORS: Record<RawCondition, string> = {
  NM: '#3b82f6',
  LP: '#22c55e', 
  MP: '#eab308',
  HP: '#f97316',
  DM: '#ef4444'
};

export default function ScrydexPricing({ cardId }: ScrydexPricingProps) {
  const { currency } = useCurrencyStore();
  const [loading, setLoading] = useState(true);
  const [fetching, setFetching] = useState(false);
  const [fetchMessage, setFetchMessage] = useState<{ type: 'success' | 'error' | 'warning' | 'info'; text: string } | null>(null);
  const [prices, setPrices] = useState<CardPricingData | null>(null);
  const [priceHistory, setPriceHistory] = useState<PriceHistoryPoint[]>([]);
  const [activeTab, setActiveTab] = useState<PriceTab>('Raw');
  const [activeFinish, setActiveFinish] = useState<string>('normal');
  const [showAllGrades, setShowAllGrades] = useState(false);
  const [chartTimeFrame, setChartTimeFrame] = useState<ChartTimeFrame>('6M');
  const [activeConditions, setActiveConditions] = useState<RawCondition[]>(['NM']);
  const fetchAttempted = useRef(false);

  // Check if prices were updated today
  const wasUpdatedToday = useMemo(() => {
    if (!prices) return false;
    const lastUpdate = prices.raw[0]?.updatedAt || prices.graded[0]?.updatedAt;
    if (!lastUpdate) return false;
    
    const updateDate = new Date(lastUpdate);
    const today = new Date();
    
    return updateDate.toDateString() === today.toDateString();
  }, [prices]);

  // Function to fetch fresh data from Scrydex API
  const fetchFromApi = async () => {
    if (fetching) return;
    
    // Check if already updated today
    if (wasUpdatedToday) {
      setFetchMessage({ 
        type: 'warning', 
        text: 'Prices already updated today. Check back tomorrow!' 
      });
      setTimeout(() => setFetchMessage(null), 5000);
      return;
    }
    
    setFetching(true);
    setFetchMessage(null);
    try {
      console.log(`Fetching Scrydex data from API for: ${cardId}`);
      const result = await fetchAndStorePrices(cardId);
      
      // Re-fetch from database after API fetch
      const [pricesData, historyData] = await Promise.all([
        getCardPrices(cardId),
        getCardPriceHistory(cardId, 'raw_NM', 9999),
      ]);
      
      setPrices(pricesData);
      setPriceHistory(historyData);
      
      // Check if we actually got new data
      if (result.rawCount === 0 && result.gradedCount === 0) {
        setFetchMessage({ 
          type: 'warning', 
          text: 'No pricing data available from Scrydex for this card' 
        });
      } else {
        setFetchMessage({ 
          type: 'success', 
          text: `Updated! ${result.rawCount} raw + ${result.gradedCount} graded prices` 
        });
      }
      
      // Auto-select first tab with data
      if (pricesData) {
        if (pricesData.raw.length > 0) {
          setActiveTab('Raw');
        } else if (pricesData.graded.length > 0) {
          const firstCompany = pricesData.graded[0].company as PriceTab;
          if (TAB_ORDER.includes(firstCompany)) {
            setActiveTab(firstCompany);
          }
        }
      }
      
      // Clear message after 5 seconds
      setTimeout(() => setFetchMessage(null), 5000);
    } catch (error) {
      console.error('Error fetching from Scrydex API:', error);
      setFetchMessage({ 
        type: 'error', 
        text: 'Failed to fetch prices. Please try again.' 
      });
      setTimeout(() => setFetchMessage(null), 5000);
    } finally {
      setFetching(false);
    }
  };

  useEffect(() => {
    let isMounted = true;
    fetchAttempted.current = false;

    async function fetchData() {
      setLoading(true);
      
      try {
        const [pricesData, historyData] = await Promise.all([
          getCardPrices(cardId),
          getCardPriceHistory(cardId, 'raw_NM', 9999),
        ]);

        if (isMounted) {
          setPrices(pricesData);
          setPriceHistory(historyData);
          
          // Auto-select first tab with data
          if (pricesData) {
            if (pricesData.raw.length > 0) {
              setActiveTab('Raw');
            } else if (pricesData.graded.length > 0) {
              const firstCompany = pricesData.graded[0].company as PriceTab;
              if (TAB_ORDER.includes(firstCompany)) {
                setActiveTab(firstCompany);
              }
            }
          }
          
          // Check if data needs refresh (no data OR data wasn't updated today)
          const needsRefresh = !pricesData || (() => {
            const lastUpdate = pricesData?.raw[0]?.updatedAt || pricesData?.graded[0]?.updatedAt;
            if (!lastUpdate) return true;
            const updateDate = new Date(lastUpdate);
            const today = new Date();
            return updateDate.toDateString() !== today.toDateString();
          })();
          
          // Auto-fetch if data is missing or stale
          if (needsRefresh && !fetchAttempted.current) {
            fetchAttempted.current = true;
            // Check if we have API configured (using proxy now)
            const hasSupabaseUrl = Boolean(import.meta.env.VITE_SUPABASE_URL);
            if (hasSupabaseUrl) {
              setFetching(true);
              setFetchMessage({ type: 'info', text: 'Updating prices...' });
              try {
                await fetchAndStorePrices(cardId);
                // Re-fetch from database after API fetch
                const [newPricesData, newHistoryData] = await Promise.all([
                  getCardPrices(cardId),
                  getCardPriceHistory(cardId, 'raw_NM', 9999),
                ]);
                if (isMounted) {
                  setPrices(newPricesData);
                  setPriceHistory(newHistoryData);
                  setFetchMessage({ type: 'success', text: 'Prices updated!' });
                  setTimeout(() => setFetchMessage(null), 3000);
                  
                  if (newPricesData) {
                    if (newPricesData.raw.length > 0) {
                      setActiveTab('Raw');
                    } else if (newPricesData.graded.length > 0) {
                      const firstCompany = newPricesData.graded[0].company as PriceTab;
                      if (TAB_ORDER.includes(firstCompany)) {
                        setActiveTab(firstCompany);
                      }
                    }
                  }
                }
              } catch (apiError) {
                console.error('Error auto-fetching from Scrydex API:', apiError);
                if (isMounted) {
                  setFetchMessage({ type: 'error', text: 'Could not update prices' });
                  setTimeout(() => setFetchMessage(null), 3000);
                }
              } finally {
                if (isMounted) {
                  setFetching(false);
                }
              }
            }
          }
        }
      } catch (error) {
        console.error('Error fetching Scrydex pricing:', error);
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    }

    fetchData();

    return () => {
      isMounted = false;
    };
  }, [cardId]);

  // Get unique finishes from pricing data
  const availableFinishes = useMemo(() => {
    if (!prices) return [];
    const finishSet = new Set<string>();
    prices.raw.forEach(r => finishSet.add(r.finish || 'normal'));
    prices.graded.forEach(g => finishSet.add(g.finish || 'normal'));
    const order = ['normal', 'holofoil', 'reverseHolofoil', 'energyReverseHolofoil', 'pokeBallReverseHolofoil', 'masterBallReverseHolofoil'];
    return [...finishSet].sort((a, b) => {
      const ai = order.indexOf(a);
      const bi = order.indexOf(b);
      return (ai === -1 ? 999 : ai) - (bi === -1 ? 999 : bi);
    });
  }, [prices]);

  // Auto-select first available finish when data loads
  useEffect(() => {
    if (availableFinishes.length > 0 && !availableFinishes.includes(activeFinish)) {
      setActiveFinish(availableFinishes[0]);
    }
  }, [availableFinishes]);

  // Broadcast finishes so collection buttons elsewhere on the page can update
  useEffect(() => {
    if (availableFinishes.length > 0) {
      document.dispatchEvent(new CustomEvent('scrydexFinishesLoaded', {
        detail: { cardId, finishes: availableFinishes }
      }));
    }
  }, [availableFinishes, cardId]);

  // Get available tabs for the active finish
  const availableTabs = useMemo(() => {
    if (!prices) return [];
    
    const tabs: PriceTab[] = [];
    
    if (prices.raw.some(r => (r.finish || 'normal') === activeFinish)) {
      tabs.push('Raw');
    }
    
    const companies = new Set(
      prices.graded
        .filter(g => (g.finish || 'normal') === activeFinish)
        .map(g => g.company)
    );
    TAB_ORDER.slice(1).forEach(tab => {
      if (companies.has(tab as GradingCompany)) {
        tabs.push(tab);
      }
    });
    
    return tabs;
  }, [prices, activeFinish]);

  // Auto-select first available tab when finish changes
  useEffect(() => {
    if (availableTabs.length > 0 && !availableTabs.includes(activeTab)) {
      setActiveTab(availableTabs[0]);
    }
  }, [availableTabs]);

  // Get graded prices for selected company and finish
  const gradedPricesForTab = useMemo(() => {
    if (!prices || activeTab === 'Raw') return [];
    
    return prices.graded
      .filter(g => g.company === activeTab && (g.finish || 'normal') === activeFinish && !g.isPerfect && !g.isSigned && !g.isError)
      .sort((a, b) => parseFloat(b.grade) - parseFloat(a.grade));
  }, [prices, activeTab, activeFinish]);

  // Raw prices filtered by active finish
  const rawPricesForFinish = useMemo(() => {
    if (!prices) return [];
    return prices.raw.filter(r => (r.finish || 'normal') === activeFinish);
  }, [prices, activeFinish]);

  // Fetch finish-specific price history when finish changes
  const [finishHistory, setFinishHistory] = useState<PriceHistoryPoint[]>([]);
  useEffect(() => {
    if (!prices) return;
    const historyKey = activeFinish === 'normal' ? 'raw_NM' : `raw_NM_${activeFinish}`;
    getCardPriceHistory(cardId, historyKey, 9999).then(setFinishHistory);
  }, [cardId, activeFinish, prices]);

  // Filter history to selected time frame
  const filterByTimeFrame = (data: PriceHistoryPoint[]): PriceHistoryPoint[] => {
    if (chartTimeFrame === 'ALL' || !data.length) return data;
    const cutoff = new Date();
    cutoff.setDate(cutoff.getDate() - TIME_FRAME_DAYS[chartTimeFrame]);
    return data.filter(d => new Date(d.date) >= cutoff);
  };

  // Build chart lines for all active conditions
  const chartLines = useMemo((): ChartLine[] => {
    return activeConditions.map(condition => {
      const condPrice = rawPricesForFinish.find(r => r.condition === condition);
      const color = CONDITION_COLORS[condition];

      // For NM, prefer real stored history
      if (condition === 'NM') {
        const history = activeFinish === 'normal' ? priceHistory : finishHistory;
        if (history && history.length >= 2) {
          const filtered = filterByTimeFrame(history);
          if (filtered.length >= 2) return { label: condition, color, data: filtered };
          return { label: condition, color, data: history.slice(-2) };
        }
      }

      // Generate synthetic history from trends for this condition
      if (condPrice?.market) {
        const synthetic = generateSyntheticHistory(condPrice.market, condPrice.trends);
        if (synthetic.length >= 2) {
          const filtered = filterByTimeFrame(synthetic);
          if (filtered.length >= 2) return { label: condition, color, data: filtered };
          return { label: condition, color, data: synthetic };
        }
      }

      return null;
    }).filter(Boolean) as ChartLine[];
  }, [activeConditions, rawPricesForFinish, priceHistory, finishHistory, activeFinish, chartTimeFrame]);

  // Loading state
  if (loading || fetching) {
    return (
      <div className="bg-black/40 backdrop-blur-sm border border-white/10 p-5 rounded-xl">
        <h3 className="text-lg font-semibold mb-4 text-white">Prices</h3>
        <div className="animate-pulse space-y-4">
          <div className="h-10 bg-white/5 rounded-lg" />
          <div className="h-36 bg-white/5 rounded-lg" />
          <div className="grid grid-cols-5 gap-4">
            {[1,2,3,4,5].map(i => (
              <div key={i} className="h-16 bg-white/5 rounded-lg" />
            ))}
          </div>
        </div>
        {fetching && (
          <div className="text-center mt-4">
            <div className="flex items-center justify-center gap-2 text-white/60 text-sm">
              <RefreshCw size={16} className="animate-spin" />
              <span>Fetching live pricing data...</span>
            </div>
          </div>
        )}
      </div>
    );
  }

  // No data state - show option to fetch from API
  if (!prices || (prices.raw.length === 0 && prices.graded.length === 0)) {
    const hasApiKey = Boolean(import.meta.env.VITE_SCRYDEX_API_KEY);
    
    return (
      <div className="bg-black/40 backdrop-blur-sm border border-white/10 p-5 rounded-xl">
        <h3 className="text-lg font-semibold mb-4 text-white">Prices</h3>
        <div className="text-center py-8">
          <Info size={32} className="mx-auto text-white/20 mb-3" />
          <p className="text-white/40 mb-4">
            Pricing data not available for this card
          </p>
          {hasApiKey && (
            <button
              onClick={fetchFromApi}
              disabled={fetching}
              className="inline-flex items-center gap-2 px-4 py-2 bg-purple-500/20 hover:bg-purple-500/30 border border-purple-500/30 rounded-lg text-purple-300 text-sm font-medium transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <RefreshCw size={16} className={fetching ? 'animate-spin' : ''} />
              {fetching ? 'Fetching...' : 'Fetch Prices'}
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-black/40 backdrop-blur-sm border border-white/10 p-3 sm:p-5 rounded-xl">
      <div className="flex items-center justify-between mb-3 sm:mb-4">
        <h3 className="text-base sm:text-lg font-semibold text-white">Prices</h3>
        <button
          onClick={fetchFromApi}
          disabled={fetching || wasUpdatedToday}
          className={`p-1.5 rounded transition-all disabled:opacity-50 disabled:cursor-not-allowed ${
            wasUpdatedToday 
              ? 'text-green-500/60' 
              : 'text-white/40 hover:text-white/80 hover:bg-white/5'
          }`}
          title={wasUpdatedToday ? 'Prices already updated today' : 'Refresh prices'}
        >
          <RefreshCw size={14} className={fetching ? 'animate-spin' : ''} />
        </button>
      </div>

      {/* Finish tabs */}
      {availableFinishes.length > 1 && (
        <div className="flex gap-1.5 mb-3 sm:mb-4 overflow-x-auto scrollbar-none">
          {availableFinishes.map(finish => (
            <button
              key={finish}
              onClick={() => { setActiveFinish(finish); setShowAllGrades(false); }}
              className={`px-2.5 sm:px-3 py-1 sm:py-1.5 rounded-full text-[10px] sm:text-xs font-medium transition-all whitespace-nowrap flex-shrink-0 ${
                activeFinish === finish
                  ? 'bg-purple-500/20 border border-purple-500/30 text-purple-300'
                  : 'bg-white/5 border border-white/10 text-white/50 hover:text-white/80 hover:bg-white/10'
              }`}
            >
              {getFinishLabel(finish)}
            </button>
          ))}
        </div>
      )}

      {/* Price type tabs (Raw / PSA / CGC / ...) */}
      {availableTabs.length > 0 && (
        <div className="flex gap-1 mb-4 sm:mb-5 p-1 bg-white/5 rounded-lg overflow-x-auto scrollbar-none">
          {availableTabs.map(tab => (
            <button
              key={tab}
              onClick={() => { setActiveTab(tab); setShowAllGrades(false); }}
              className={`px-2.5 sm:px-4 py-1.5 sm:py-2 rounded-md text-xs sm:text-sm font-medium transition-all flex items-center gap-1.5 sm:gap-2 whitespace-nowrap flex-shrink-0 ${
                activeTab === tab
                  ? 'bg-white/10 text-white'
                  : 'text-white/50 hover:text-white/80 hover:bg-white/5'
              }`}
            >
              {tab !== 'Raw' && <GradingIcon company={tab} size={16} />}
              {tab}
            </button>
          ))}
        </div>
      )}

      {/* Raw Prices Tab */}
      {activeTab === 'Raw' && rawPricesForFinish.length > 0 && (
        <>
          {/* Chart */}
          <div className="mb-4 sm:mb-6 bg-white/5 rounded-lg p-2 sm:p-4">
            <div className="flex items-center gap-1 mb-2">
              {(['7D', '1M', '3M', '6M', '1Y', 'ALL'] as ChartTimeFrame[]).map(frame => (
                <button
                  key={frame}
                  onClick={() => setChartTimeFrame(frame)}
                  className={`px-2 py-1 rounded text-xs font-medium transition-colors ${
                    chartTimeFrame === frame
                      ? 'bg-white/15 text-white'
                      : 'text-white/40 hover:text-white/70'
                  }`}
                >
                  {frame}
                </button>
              ))}
            </div>
            <PriceChart lines={chartLines} />
          </div>

          {/* Condition toggles with prices */}
          <div className="flex flex-wrap items-stretch gap-2">
            {(['NM', 'LP', 'MP', 'HP', 'DM'] as RawCondition[]).map(condition => {
              const condPrice = rawPricesForFinish.find(p => p.condition === condition);
              if (!condPrice) return null;
              const isActive = activeConditions.includes(condition);
              return (
                <button
                  key={condition}
                  onClick={() => {
                    setActiveConditions(prev =>
                      isActive
                        ? prev.length > 1 ? prev.filter(c => c !== condition) : prev
                        : [...prev, condition]
                    );
                  }}
                  className={`flex flex-col items-center rounded-lg px-3 py-2 transition-all border min-w-[80px] ${
                    isActive
                      ? 'text-white'
                      : 'text-white/30 border-white/10 hover:text-white/60 hover:border-white/20'
                  }`}
                  style={isActive ? { backgroundColor: CONDITION_COLORS[condition] + '15', borderColor: CONDITION_COLORS[condition] + '40' } : undefined}
                >
                  <span className="flex items-center gap-1.5 mb-1">
                    <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: CONDITION_COLORS[condition], opacity: isActive ? 1 : 0.4 }} />
                    <span className="text-[10px] font-medium uppercase tracking-tight">{CONDITION_LABELS[condition]}</span>
                  </span>
                  <span className={`text-sm font-bold ${isActive ? 'text-white' : 'text-white/40'}`}>
                    {formatPrice(condPrice.market, currency)}
                  </span>
                  {condPrice.low !== null && condPrice.low !== condPrice.market && (
                    <span className="text-[10px] text-white/30">{formatPrice(condPrice.low, currency)}</span>
                  )}
                </button>
              );
            })}
          </div>

          {/* 30-day trend */}
          {rawPricesForFinish.find(r => r.condition === 'NM')?.trends?.days_30 && (
            <div className="mt-3 sm:mt-4 pt-3 sm:pt-4 border-t border-white/10 flex items-center justify-between text-xs sm:text-sm">
              <span className="text-white/40">30-day change</span>
              <TrendIndicator 
                percentChange={rawPricesForFinish.find(r => r.condition === 'NM')!.trends!.days_30!.percent_change} 
                size="md"
              />
            </div>
          )}
        </>
      )}

      {/* Graded Prices Tab */}
      {activeTab !== 'Raw' && gradedPricesForTab.length > 0 && (
        <div className="space-y-0.5 sm:space-y-1">
          {(showAllGrades ? gradedPricesForTab : gradedPricesForTab.slice(0, 8)).map((price, idx) => (
            <GradedPriceRow
              key={`${price.grade}-${idx}`}
              grade={price.grade}
              marketPrice={price.market}
              lowPrice={price.low}
              highPrice={price.high}
              trends={price.trends}
              currency={currency}
            />
          ))}
          
          {gradedPricesForTab.length > 8 && (
            <div className="text-center pt-2">
              <button 
                onClick={() => setShowAllGrades(!showAllGrades)}
                className="text-xs text-purple-400 hover:text-purple-300 transition-colors cursor-pointer"
              >
                {showAllGrades 
                  ? 'Show less' 
                  : `+${gradedPricesForTab.length - 8} more grades`
                }
              </button>
            </div>
          )}
        </div>
      )}

      {/* Graded tab but no data */}
      {activeTab !== 'Raw' && gradedPricesForTab.length === 0 && (
        <div className="text-center py-8">
          <Info size={24} className="mx-auto text-white/20 mb-2" />
          <p className="text-white/40 text-sm">
            No {activeTab} graded prices available
          </p>
        </div>
      )}

      {/* Fetch Message */}
      {fetchMessage && (
        <div className={`mt-4 px-3 py-2 rounded-lg text-xs text-center ${
          fetchMessage.type === 'success' ? 'bg-green-500/20 text-green-300 border border-green-500/30' :
          fetchMessage.type === 'warning' ? 'bg-yellow-500/20 text-yellow-300 border border-yellow-500/30' :
          'bg-red-500/20 text-red-300 border border-red-500/30'
        }`}>
          {fetchMessage.text}
        </div>
      )}

      {/* Footer */}
      <p className="text-xs text-white/30 mt-4 text-center">
        Last updated {new Date(prices.raw[0]?.updatedAt || prices.graded[0]?.updatedAt || '').toLocaleDateString('en-US', { 
          month: 'short', 
          day: 'numeric', 
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        })}
      </p>
    </div>
  );
}
